import { useState, useRef } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * Question card rendered by the AI agent when it needs user clarification.
 *
 * Single-select: click an option to submit instantly.
 * Multi-select: toggle options then press the submit button.
 * Always shows an "Other" free-text row.
 *
 * onSubmit receives a plain text string (the selected label or free text).
 */
export function UserQuestionCard({ question, onSubmit }) {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [freeText, setFreeText] = useState('');
  const inputRef = useRef(null);

  const isMultiSelect = question.multi_select;

  const handleOptionClick = (label) => {
    if (!isMultiSelect) {
      onSubmit(label);
      return;
    }
    setFreeText('');
    setSelectedOptions((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const handleFreeTextKeyDown = (e) => {
    if (e.key === 'Enter' && freeText.trim()) {
      e.preventDefault();
      if (isMultiSelect) {
        handleMultiSubmit();
      } else {
        onSubmit(freeText.trim());
      }
    }
  };

  const handleMultiSubmit = () => {
    const parts = [...selectedOptions];
    if (freeText.trim()) parts.push(freeText.trim());
    if (parts.length > 0) onSubmit(parts.join(', '));
  };

  const canSubmitMulti = selectedOptions.length > 0 || freeText.trim();

  return (
    <div className="flex gap-3 justify-start py-4">
      <div className="flex-shrink-0 flex items-center justify-center size-8 rounded-full bg-purple-100 mt-0.5">
        <Icon icon="mdi:help-circle-outline" className="size-4 text-purple-600" />
      </div>
      <div className="max-w-md">
        <p className="text-sm font-semibold text-gray-800 mb-2">
          {question.question_text}
        </p>

        <div className="space-y-1">
          {question.options.map((option, index) => {
            const isSelected = selectedOptions.includes(option.label);
            return (
              <button
                key={index}
                onClick={() => handleOptionClick(option.label)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors cursor-pointer group flex items-start gap-2 ${
                  isMultiSelect && isSelected
                    ? 'bg-purple-50 text-purple-700'
                    : 'hover:bg-purple-50 hover:text-purple-700 text-gray-700'
                }`}
              >
                {isMultiSelect ? (
                  <Icon
                    icon={isSelected ? 'mdi:checkbox-marked-circle' : 'mdi:checkbox-blank-circle-outline'}
                    className={`size-4 mt-0.5 flex-shrink-0 ${isSelected ? 'text-purple-600' : 'text-gray-300 group-hover:text-purple-400'}`}
                  />
                ) : (
                  <span className="text-xs font-medium text-gray-400 group-hover:text-purple-500 mt-0.5 w-4 text-right flex-shrink-0">
                    {index + 1}.
                  </span>
                )}
                <div className="min-w-0">
                  <span className="text-sm font-medium">{option.label}</span>
                  {option.description && (
                    <p className="text-xs text-gray-400 group-hover:text-purple-400 mt-0.5 leading-tight">
                      {option.description}
                    </p>
                  )}
                </div>
              </button>
            );
          })}

          {/* "Other" free-text option */}
          <div className="flex items-center gap-2 px-3 py-2">
            {isMultiSelect ? (
              <Icon
                icon={freeText.trim() ? 'mdi:checkbox-marked-circle' : 'mdi:checkbox-blank-circle-outline'}
                className={`size-4 flex-shrink-0 ${freeText.trim() ? 'text-purple-600' : 'text-gray-300'}`}
              />
            ) : (
              <span className="text-xs font-medium text-gray-400 w-4 text-right flex-shrink-0">
                {question.options.length + 1}.
              </span>
            )}
            <span className="text-sm text-gray-500 flex-shrink-0">{t('ai.userQuestion.other')}</span>
            <input
              ref={inputRef}
              type="text"
              value={freeText}
              onChange={(e) => {
                setFreeText(e.target.value);
                if (!isMultiSelect) setSelectedOptions([]);
              }}
              onKeyDown={handleFreeTextKeyDown}
              placeholder={t('ai.userQuestion.answerPlaceholder')}
              className="flex-1 text-sm border-b border-gray-300 focus:border-purple-500 outline-none bg-transparent py-0.5 min-w-0"
            />
            {!isMultiSelect && (
              <button
                type="button"
                onClick={() => freeText.trim() && onSubmit(freeText.trim())}
                disabled={!freeText.trim()}
                aria-label={t('ai.userQuestion.sendAnswer')}
                className="flex items-center justify-center size-7 rounded-md text-purple-600 hover:bg-purple-50 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed cursor-pointer"
              >
                <Icon icon="mdi:send" className="size-4" />
              </button>
            )}
          </div>
        </div>

        {/* Submit button only for multi-select */}
        {isMultiSelect && (
          <button
            onClick={handleMultiSubmit}
            disabled={!canSubmitMulti}
            className={`mt-3 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
              canSubmitMulti
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {t('ai.userQuestion.submitAnswer')}
          </button>
        )}
      </div>
    </div>
  );
}

UserQuestionCard.propTypes = {
  question: PropTypes.shape({
    question_text: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
      }),
    ).isRequired,
    multi_select: PropTypes.bool,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
};
